import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  TextField,
  Autocomplete,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Stack,
  Tooltip,
  IconButton,
  InputAdornment,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { debounce, isEmpty } from "lodash";
import useFetchEmployeesList from "src/Pages/Company/OrganizationStructure/api/useFetchEmployeesList";
import __ from "src/utils/Translations";
import TransHtml from "src/Components/TransHtml";
import useFetchAreaMembership from "src/Pages/Company/OrganizationStructure/api/useFetchAreaMembership";
import { Avatar, PresenceIndicator } from "@benefit-systems/hr-tools-ui";
import useFetchStructureData from "src/Pages/Company/OrganizationStructure/api/useFetchStructureData";
import { IAreaMembers } from "src/Pages/Company/OrganizationStructure/Areas/Area.models";
import { useParams } from "react-router-dom";
import useCheckCanBeAreaMembersManager from "src/Pages/Company/OrganizationStructure/api/useCheckCanBeAreaMembersManager";
import { AxiosError } from "axios";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";

interface IAutocompleteAreas {
  companyId: string;
  structureId: string;
  helperText?: string;
  isEdit?: boolean;
  dataTestId?: string;
}

const AutocompleteManager: FC<IAutocompleteAreas> = ({
  companyId,
  structureId,
  helperText,
  isEdit,
  dataTestId,
}) => {
  const { control, watch, setValue, clearErrors } = useFormContext();
  const [search, setSearch] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const { areaId } = useParams<{ areaId: string }>();
  const [isRemovedAdornment, setIsRemovedAdornment] = useState(!isEdit);
  const [isValidating, setIsValidating] = useState(false);

  const managerCurrentData = watch("manager");
  const isChecked = watch("managerAsMember");
  const membersWatch = watch("members");
  const parentWatch = watch("parent");

  const isDisabled =
    !managerCurrentData ||
    managerCurrentData?.id === null ||
    managerCurrentData?.id === "structure";

  const [selectedManagerAvatar, setSelectedManagerAvatar] = useState<
    string | undefined
  >();

  const { mutate: checkMembers, data: validationData } =
    useCheckCanBeAreaMembersManager(companyId, managerCurrentData?.id || "");

  const givenEmployeeCanInheritManager =
    validationData?.givenEmployeeCanInheritManager;

  const { data: structureData, isFetching: structureDataIsFetching } =
    useFetchStructureData(companyId, structureId);

  const structureAvatar = structureData?.avatarLink;

  const enabled: boolean = !!companyId && !!structureId;
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useFetchEmployeesList({
      companyId,
      structureId,
      enabled,
      filterString: search,
    });

  const { data: employeeAreaMembership, error: membershipError } =
    useFetchAreaMembership(companyId, managerCurrentData?.id);

  const logStatus = useLogStatus();

  if (membershipError) {
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "AutocompleteManager: fetchAreaMembership failed",
      statusDetails: {
        source: "API",
        operation: "fetchAreaMembership",
        status: (membershipError as AxiosError)?.response?.status,
        url: (membershipError as AxiosError)?.config?.url,
      },
    });
  }

  useEffect(() => {
    if (managerCurrentData?.id === "structure") {
      setSelectedManagerAvatar(structureAvatar);
    } else {
      setSelectedManagerAvatar(managerCurrentData?.avatar);
    }
  }, [managerCurrentData, structureAvatar]);

  useEffect(() => {
    const currentManagerId = managerCurrentData?.id;

    if (currentManagerId && currentManagerId !== "structure") {
      setIsValidating(true);
      checkMembers({
        members: membersWatch?.map((member: IAreaMembers) => member.id) || [],
        parentArea: parentWatch?.id || null,
      });
    } else {
      setIsValidating(false);
    }
  }, [managerCurrentData?.id, membersWatch, parentWatch]);

  useEffect(() => {
    if (validationData !== undefined) {
      setIsValidating(false);
    }
  }, [validationData]);

  useEffect(() => {
    if (givenEmployeeCanInheritManager === false && isChecked) {
      setValue("managerAsMember", false, { shouldDirty: true });
    }
  }, [givenEmployeeCanInheritManager, isChecked, setValue]);

  const options = useMemo(() => {
    const baseOptions = [
      {
        name: structureData?.name,
        id: "structure",
        avatar: structureData?.avatarLink ?? undefined,
        position: null,
        initials: null,
        fk: null,
        presenceInformation: null,
      },
      ...(data?.pages.flatMap((page) => page.content) || []),
    ];

    if (
      managerCurrentData &&
      managerCurrentData.id !== "structure" &&
      managerCurrentData.id !== null
    ) {
      const managerExistsInOptions = baseOptions.some(
        (opt) => opt.id === managerCurrentData.id
      );

      if (!managerExistsInOptions) {
        return [
          baseOptions[0],
          {
            id: managerCurrentData.id,
            name: managerCurrentData.name,
            avatar: managerCurrentData.avatar,
            position: managerCurrentData.position,
            initials: managerCurrentData.initials,
            fk: managerCurrentData.fk,
            presenceInformation: managerCurrentData.presenceInformation,
          },
          ...baseOptions.slice(1),
        ];
      }
    }

    return baseOptions;
  }, [data?.pages, structureData, managerCurrentData]);

  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearch(value);
    }, 300)
  ).current;

  const handleSearchChange = useCallback(
    (event: any, newInputValue: string, reason: string) => {
      if (reason !== "reset") {
        setIsTyping(true);
        debouncedSearch(newInputValue);
      }
    },
    [debouncedSearch]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleScroll = (event: any) => {
    const listbox = event.currentTarget;
    const scrollPercentage =
      (listbox.scrollTop + listbox.clientHeight) / listbox.scrollHeight;

    if (scrollPercentage > 0.9 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const resetAvatarAndPosition = () => {
    setSelectedManagerAvatar(structureAvatar);
  };

  if (structureDataIsFetching) {
    return <Skeleton width="100%" height={56} />;
  }

  return (
    <>
      <Controller
        name="manager"
        control={control}
        render={({ field: { onChange, value }, fieldState }) => (
          <Autocomplete
            getOptionLabel={(option) =>
              !isEmpty(option)
                ? `${option.name} ${option.fk ? `(FK: ${option.fk})` : ""}`
                : ""
            }
            options={options}
            loading={isLoading || isFetchingNextPage}
            disableListWrap
            disablePortal
            value={value || null}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onOpen={() => setSearch("")}
            onChange={(_, newValue) => {
              setIsTyping(false);
              clearErrors("manager");
              debouncedSearch.cancel();
              setSearch("");

              if (!newValue) {
                setIsRemovedAdornment(true);
                setValue("manager", null, { shouldDirty: true });
                setValue("managerAsMember", false, { shouldDirty: true });
                resetAvatarAndPosition();
              } else {
                setIsRemovedAdornment(false);
                setSelectedManagerAvatar(newValue.avatar);

                const wasPreviouslyMember = Array.isArray(membersWatch)
                  ? membersWatch.some(
                      (member: IAreaMembers) => member.id === newValue.id
                    )
                  : false;

                const updatedMembers = Array.isArray(membersWatch)
                  ? membersWatch.filter(
                      (member: IAreaMembers) => member.id !== newValue.id
                    )
                  : [];

                setValue("members", updatedMembers, { shouldDirty: true });
                setValue("manager", newValue, { shouldDirty: true });

                if (wasPreviouslyMember) {
                  setValue("managerAsMember", true, { shouldDirty: true });
                }
              }
            }}
            onInputChange={handleSearchChange}
            onBlur={() => setIsTyping(false)}
            filterOptions={(options) => options}
            renderInput={(params) => (
              <TextField
                {...params}
                data-testid={dataTestId}
                id="manager"
                label={__("Osoba zarządzająca")}
                error={fieldState.invalid}
                helperText={fieldState.error?.message || helperText}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "56px",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "14px",
                    lineHeight: "14px",
                    fontWeight: 700,
                    minHeight: "56px",
                    // eslint-disable-next-line no-nested-ternary
                    marginTop: isTyping
                      ? "0"
                      : managerCurrentData?.position
                      ? "-12px"
                      : "0",
                  },
                }}
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps?.input,
                    startAdornment: (
                      <InputAdornment position="start">
                        {!isTyping && !isRemovedAdornment && (
                          <>
                            <PresenceIndicator
                              status={
                                managerCurrentData?.presenceInformation?.status
                              }
                            >
                              <Avatar
                                src={selectedManagerAvatar}
                                size={6}
                                alt={__("avatar managera")}
                              >
                                {managerCurrentData?.initials ?? ""}
                              </Avatar>
                            </PresenceIndicator>
                            {managerCurrentData?.position && (
                              <Typography
                                variant="caption"
                                color="gray"
                                sx={{
                                  fontSize: "12px",
                                  lineHeight: "12px",
                                  position: "absolute",
                                  left: "46px",
                                  top: "31px",
                                }}
                              >
                                {typeof managerCurrentData.position === "object"
                                  ? managerCurrentData.position?.label
                                  : managerCurrentData.position}
                              </Typography>
                            )}
                          </>
                        )}
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {isLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {(params.slotProps?.input as any)?.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <ListItem
                {...props}
                key={option.id}
                alignItems="center"
                sx={{ display: "flex", alignItems: "center", paddingY: "6px" }}
              >
                <ListItemAvatar>
                  <PresenceIndicator
                    status={option?.presenceInformation?.status}
                  >
                    <Avatar
                      src={option.avatar ?? undefined}
                      alt={__("avatar managera")}
                    >
                      {option.initials ?? ""}
                    </Avatar>
                  </PresenceIndicator>
                </ListItemAvatar>
                <ListItemText
                  sx={{ ml: 1 }}
                  primary={
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, fontSize: "14px" }}
                    >
                      {`${option.name}${
                        option.fk ? ` (FK: ${option.fk})` : ""
                      }`}
                    </Typography>
                  }
                  secondary={
                    option.position?.label && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "12px" }}
                      >
                        {option.position?.label}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            )}
            slotProps={{
              listbox: {
                onScroll: handleScroll,
                style: {
                  maxHeight: "300px",
                  overflowY: "auto",
                },
              },
            }}
          />
        )}
      />
      <Controller
        name="managerAsMember"
        control={control}
        defaultValue={false}
        render={({ field }) => (
          <Box>
            <Box sx={{ display: "inline-flex", alignItems: "center" }}>
              <FormGroup sx={{ m: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      data-testId="os__form-inherit-checkbox"
                      disabled={
                        isDisabled ||
                        givenEmployeeCanInheritManager === false ||
                        isValidating
                      }
                      {...field}
                      checked={field.value}
                    />
                  }
                  label={
                    <Stack direction="row" sx={{ alignItems: "center" }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {__("Osoba zarządzająca będzie członkiem tego obszaru")}
                      </Typography>
                      <Tooltip
                        placement="top"
                        title={__(
                          "Jedna osoba może zarządzać wieloma obszarami, ale może być członkiem tylko jednego z nich. Nazwa obszaru do którego należy osoba zarządzająca będzie wyświetlana na jej karcie pracownika."
                        )}
                      >
                        <IconButton
                          sx={{
                            background: "#24272A",
                            borderRadius: "8px",
                            width: "21px",
                            height: "15px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginLeft: "16px",
                            fontWeight: "800",
                            fontSize: "10px",
                            lineHeight: "15px",
                            color: "#FFF",
                          }}
                        >
                          ?
                        </IconButton>
                      </Tooltip>
                      {isValidating && (
                        <CircularProgress
                          color="inherit"
                          size={16}
                          sx={{ marginLeft: "8px" }}
                        />
                      )}
                    </Stack>
                  }
                />
              </FormGroup>
            </Box>
            <Typography color="warning.dark">
              {!isValidating &&
                employeeAreaMembership &&
                employeeAreaMembership?.areaId !== areaId &&
                isChecked && (
                  <Typography>
                    <TransHtml
                      text="{0} jest obecnie członkiem obszaru {1}. Po zapisaniu stanie się członkiem obecnego obszaru."
                      args={[
                        managerCurrentData?.name ?? "",
                        employeeAreaMembership?.areaName ?? "",
                      ]}
                    />
                  </Typography>
                )}
            </Typography>
            {!isValidating && givenEmployeeCanInheritManager === false && (
              <Typography color="error">
                <TransHtml
                  text="{0} nie może być członkiem tego obszaru, ponieważ powstanie konflikt przełożonych."
                  args={[managerCurrentData?.name ?? ""]}
                />
              </Typography>
            )}
          </Box>
        )}
      />
    </>
  );
};

export default AutocompleteManager;
